import { Box, Button, Container, Icon, Switch, Toolbar, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { AddOutlined } from '@material-ui/icons'
import clsx from 'clsx'
import moment from 'moment'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import useCurrentUser from '../../../hooks/useCurrentUser'
import {
	addAdditionalDogs,
	addMembership,
	getMembershipTypes,
	updateHouseholdFoundingTag,
	updateMembership,
	updateMuteRenewalAlert
} from '../../../actions/households/households.actions'
import AddDogsDialog from '../../../components/Dialogs/Membership/AddDogsDialog'
import RemoveDogsDialog from '../../../components/Dialogs/Membership/RemoveDogsDialog'
import AddMembershipDialog from '../../../components/Dialogs/Membership/AddMembershipDialog'
import UpgradeMembershipDialog from '../../../components/Dialogs/Membership/UpgradeMembershipDialog'
import DelayMembershipDialog from '../../../components/Dialogs/Membership/DelayMembershipDialog'
import EditAutoRenewDialog from '../../../components/Dialogs/Membership/EditAutoRenewDialog'
import EditMembershipDialog from '../../../components/Dialogs/Membership/EditMembershipDialog'
import ExtendMembershipDialog from '../../../components/Dialogs/Membership/ExtendMembershipDialog'
import SpecialRenewalDialog from '../../../components/Dialogs/Membership/SpecialRenewalDialog'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'
import { CheckInsTable } from '../../../components/Tables/CheckInsTable'
import { getValidMemberships, isBasicMembership, getBasicMembershipInfo } from '../../../utils'
import { filterMembershipTypesForHousehold } from '../../../utils/membershipTypes'

const membershipUpgradeMap = [
	{
		type: 'vip_trial',
		level: 0
	},
	{
		type: 'bar_annual',
		level: 2
	},
	{
		type: 'bar_monthly',
		level: 1
	},
	{
		type: 'vip_annual',
		level: 4
	},
	{
		type: 'vip_monthly',
		level: 3
	},
	{
		type: 'pet_care_annual',
		level: 2
	},
	{
		type: 'pet_care_monthly',
		level: 1
	},
	{
		type: 'founding',
		level: 5
	},
	{ type: 'grooming_annual', level: 2 },
	{ type: 'grooming_monthly', level: 1 }
]

const useStyles = makeStyles((theme) => ({
	switchRoot: {
		width: 51,
		height: 31,
		padding: 0
	},
	switchBase: {
		padding: 0,
		margin: 2,
		'&$checked': {
			transform: 'translateX(20px)',
			color: theme.palette.common.white,
			'& + $track': {
				backgroundColor: '#6B4AD6',
				opacity: 1
			},
			'& .far::before': {
				color: '#6B4AD6'
			}
		},
		'&$focusVisible $thumb': {
			color: '#6B4AD6'
			//border: "6px solid #fff",
		},
		'&$checked + $track': {
			backgroundColor: '#6B4AD6',
			opacity: 1
		},
		'&$focusVisible $thumb': {
			color: '#6B4AD6',
			opacity: 1
		},
		'&.MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
			backgroundColor: '#6B4AD6',
			opacity: 1
		},
		'&.MuiSwitch-switchBase.Mui-checked .far.fa-check::before': {
			color: '#6B4AD6'
		}
	},
	thumb: {
		width: 27,
		height: 27,
		background: '#F3F3F3',
		border: '1px solid #E5E5E5',
		borderRadius: '50%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'& .far': {
			color: '#A2A1A4',
			fontSize: '13px',
			lineHeight: '15px'
		}
	},
	track: {
		borderRadius: 100,
		padding: 2,
		backgroundColor: '#D0CFD2',
		opacity: 1,
		transition: theme.transitions.create(['background-color'])
	},
	container: {
		padding: '24px',
		paddingTop: 0
	},
	toolbar: {
		// marginBottom: 24,
		marginTop: 6,
		marginBottom: 6,
		backgroundColor: 'transparent'
	},
	iconButton: {
		padding: 0
	},
	icon: {
		display: 'flex',
		justifyContent: 'center',
		'&::before': {
			fontSize: '16px',
			color: '#777679',
			lineHeight: '27px'
		}
	},
	headerButton: {
		fontFamily: 'Nunito',
		fontWeight: '700',
		marginLeft: 16,
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	addButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		},
		'&.Mui-disabled .MuiButton-label .MuiButton-startIcon .MuiSvgIcon-root': {
			fill: 'rgba(0, 0, 0, 0.12) !important'
		}
	},
	groupBody: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		alignItems: 'space-evenly',
		padding: 24,
		backgroundColor: '#F3F3F3'
	},
	groupCol: {
		width: '24%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		'& p': {
			margin: 0
		},
		'& .change': {
			fontFamily: 'Nunito',
			fontSize: 14,
			fontWeight: '600',
			color: '#6B4AD6',
			lineHeight: '23px',
			cursor: 'pointer'
		},
		'& .char': {
			fontFamily: 'Nunito',
			fontSize: 14,
			fontWeight: '600',
			color: '#6B4AD6',
			lineHeight: '23px',
			cursor: 'pointer',
			backgroundColor: 'rgba(107, 74, 214, 0.1)',
			padding: 4,
			borderRadius: 6,
			marginRight: 4,
			marginBottom: 4
		}
	},
	colTitle: {
		fontFamily: 'Nunito',
		fontSize: 12,
		fontWeight: '800',
		letterSpacing: 0.75,
		color: '#1A1A35',
		marginBottom: 5,
		'&.disabled': {
			color: '#777679'
		}
	},
	mainText: {
		fontFamily: 'Nunito',
		fontSize: '22px',
		fontWeight: '800',
		color: '#1A1A35',
		overflow: 'hidden',
		textTransform: 'capitalize',
		'&.disabled': {
			color: '#777679'
		},
		'& div': {
			// display: "flex",
			// flexDirection: "row",
			// justifyContent: "space-between"
		}
	},
	subText: {
		'&.disabled': {
			color: '#777679'
		}
	},

	editMembershipText: {
		fontFamily: 'Nunito',
		fontSize: '16px',
		fontWeight: '600',
		color: '#6B4AD6',
		overflow: 'hidden',
		paddingTop: '8px',
		textTransform: 'capitalize'
	}
}))

const Membership = (props) => {
	const classes = useStyles()
	const theme = useTheme()
	const dispatch = useDispatch()
	const history = useHistory()
	const facility = useSelector((state) => state.specific.facilities.selected)
	const client = useSelector((state) => state.specific.households.selected)
	let membershipTypesArray = useSelector((state) => state.specific.households.membershipTypes)
	const user = useSelector((state) => state.general?.user?.user)
	const [isAddMembershipDialogShown, setAddMembershipDialogShown] = useState(false)
	const [isRemoveDogDialogShown, setRemoveDogDialogShown] = useState(false)
	const [isEditDialogShown, setEditDialogShown] = useState(false)
	const [isExtendDialogShown, setExtendDialogShown] = useState(false)
	const [isAddDogDialogShown, setAddDogDialogShown] = useState(false)
	const [isEditAutoRenewDialogShown, setEditAutoRenewDialogShown] = useState(false)
	const [isUpgradeMembershipDialogShown, setUpgradeMembershipDialogShown] = useState(false)
	const [isSpecialRenewalDialogShown, setSpecialRenewalDialogShown] = useState(false)
	const [upcoming, setIsUpcoming] = useState(false)
	const [delayDialog, setDelayDialogShown] = useState(false)
	const [currentMembership, setCurrentMembership] = useState(null)
	const [membership, setMembership] = useState({})

	const [membershipTypes, setMembershipTypes] = useState([])

	const loading = useSelector((state) => state.specific.households.memberLoading)
	const houseLoading = useSelector((state) => state.specific.households.loading)

	const { isBackOfficeUser, isPcaSupervisorUser, isSupportUser } = useCurrentUser()
	const isManager = isBackOfficeUser || isSupportUser
	const canChangeMembershipAutoRenewSetting = isBackOfficeUser

	// const market = client?.market || {}

	useEffect(() => {
		if (!client?._id) return
		dispatch(
			getMembershipTypes({
				householdId: client._id,
				facilityId: client.default_facility_id
			})
		)
	}, [client?._id, client?.default_facility_id])

	const addNewMembership = (membership) => {
		membership = { ...membership, household_id: client._id }
		dispatch(addMembership(membership))
	}

	const addAdditionalDogsToMembership = (membership, membershipID) => {
		membership = { ...membership, household_id: client._id }
		dispatch(addAdditionalDogs(membership, membershipID))
	}

	const removeDogsFromMembership = (membership, membershipId) => { }

	useEffect(() => {
		if (client) {
			const ourMembershipTypes = filterMembershipTypesForHousehold(membershipTypesArray, client)
			setMembershipTypes(ourMembershipTypes)
		}
	}, [membershipTypesArray, client])

	const updateExistingMembership = (membership) => {
		dispatch(updateMembership(membership))
	}

	const updateFounding = () => {
		dispatch(
			updateHouseholdFoundingTag(client._id, { founding_member: client.founding_member ? false : true })
		)
	}

	const updateMute = () => {
		dispatch(
			updateMuteRenewalAlert(client._id, { mute_renewal_alert: client.mute_renewal_alert ? false : true })
		)
	}

	const activeMembershipsExist = () => {
		return (
			client &&
			client.memberships &&
			client.memberships.find((membership) => {
				return !membership.deactivated && !membership.expired && !membership.upcoming
			})
		)
	}

	const hasGroomingMembership = () => {
		return client?.memberships?.find(
			(membership) =>
				(membership.type === 'grooming_annual' || membership.type === 'grooming_monthly') &&
				!membership.deactivated &&
				!membership.expired &&
				!membership.upcoming
		)
	}

	const hasUpcoming = () => {
		return (
			client &&
			client.memberships &&
			client.memberships.find((membership) => {
				return !membership.deactivated && !membership.expired && membership.upcoming
			})
		)
	}

	useEffect(() => {
		if (client?.memberships?.length) {
			let active = client.memberships.find(
				(m) => !m.deactivated && !m.expired && !isBasicMembership(m) && !m.upcoming
			)
			if (active) {
				setCurrentMembership(active)
			} else {
				setCurrentMembership(null)
			}
		}
	}, [client, loading])

	const activityExists = () => {
		if (client && client.checkIns && client.checkIns.length != 0) {
			return true
		}
		return false
	}

	const renderMemberships = () => {
		let hasActiveMembership = activeMembershipsExist()
		let upcomingMembership = hasUpcoming()

		let sortedMemberships =
			client && client.memberships
				? client.memberships
					.filter((m) => {
						if (m.upcoming && (m.expired || m.deactivated)) return false
						if (hasActiveMembership && m._id === hasActiveMembership._id) return false
						if (upcomingMembership && m._id === upcomingMembership._id) return false
						return true
					})
					.sort((a, b) => {
						let compare = 0
						if (moment(a.start_date).isBefore(moment(b.start_date))) {
							compare = 1
						} else if (moment(b.start_date).isBefore(moment(a.start_date))) {
							compare = -1
						}
						return compare
					})
				: []

		let earliestMembership = sortedMemberships[sortedMemberships.length - 1]

		if (upcomingMembership) {
			sortedMemberships.unshift(upcomingMembership)
		}

		if (hasActiveMembership) {
			sortedMemberships.unshift(hasActiveMembership)
		}

		if (!hasActiveMembership && !sortedMemberships.some(isBasicMembership)) {
			sortedMemberships.unshift(getBasicMembershipInfo())
		}

		const validMemberships = getValidMemberships(sortedMemberships)

		const getRenewalStatus = (membership) => {
			if (membership.auto_renew) return 'Enabled'
			if (membership.canceled_within_notice_period) {
				if (!membership.expired) return 'Pending Cancellation'
				return 'Canceled'
			}
			return 'Disabled'
		}

		return sortedMemberships
			.map((m) => {
				return {
					...m,
					deactivated: m.deactivated || m.expired
				}
			})
			.map((membership) => {
				let type = membershipTypes.find((m) => m.type === membership.type)
				let types = membershipTypes.filter((m) => m.type === membership.type)

				if (isBasicMembership(membership))
					return (
						<div style={{ marginBottom: '16px', borderRadius: '6px' }}>
							<div className={classes.groupBody}>
								<div className={classes.groupCol} style={{ paddingRight: '5px' }}>
									<span
										className={clsx([classes.colTitle, membership.deactivated && 'disabled'])}>
										ACCESS TYPE
									</span>
									<div
										className={clsx([classes.mainText, membership.deactivated && 'disabled'])}>
										{membership.type}
									</div>
								</div>
							</div>
						</div>
					)
				return (
					<div style={{ marginBottom: '16px', borderRadius: '6px' }}>
						<div className={classes.groupBody}>
							<div className={classes.groupCol} style={{ paddingRight: '5px' }}>
								<span className={clsx([classes.colTitle, membership.deactivated && 'disabled'])}>
									ACCESS TYPE
								</span>
								<div className={clsx([classes.mainText, membership.deactivated && 'disabled'])}>
									{(type && type.name) || membership.type}{' '}
									{type && type.type !== 'vip_trial' ? type.billing_method || '' : ''}
								</div>
								<div className={clsx([classes.subText, membership.deactivated && 'disabled'])}>
									{membership.deactivated
										? 'Ended'
										: membership.upcoming
											? 'Upcoming'
											: 'Active'}
								</div>
								<Typography
									onClick={() => {
										if (!isManager) return
										setMembership(membership)
										setEditDialogShown(true)
									}}
									style={{
										opacity: isManager ? 1 : 0.6
									}}
									className={classes.editMembershipText}>
									Edit Status
								</Typography>
							</div>
							<div
								className={classes.groupCol}
								style={{
									marginRight: '50px'
									// width: membership.type === 'vip_trial' ? '30%' : '24%'
								}}>
								<span className={clsx([classes.colTitle, membership.deactivated && 'disabled'])}>
									FULL TERM
								</span>
								<div className={clsx([classes.mainText, membership.deactivated && 'disabled'])}>
									<div className={clsx([classes.subText, membership.deactivated && 'disabled'])}>
										{moment(membership.start_date).format('MMM D, YYYY') +
											' - ' +
											moment(membership.end_date).format('MMM D, YYYY')}
									</div>
								</div>
								{earliestMembership &&
									`Member since ${' ' + moment(earliestMembership.start_date).format('MMM D, YYYY')
									}`}
								{membership.upcoming ? (
									<Typography
										onClick={() => {
											if (!isManager) return
											setMembership(membership)
											setDelayDialogShown(true)
										}}
										style={{
											opacity: isManager ? 1 : 0.6
										}}
										className={classes.editMembershipText}>
										Delay Start Date
									</Typography>
								) : null}
								<Typography
									style={{
										cursor: 'pointer',
										opacity: isManager ? 1 : 0.6
									}}
									onClick={() => {
										if (!isManager) return
										setMembership(membership)
										setExtendDialogShown(true)
									}}
									className={classes.editMembershipText}>
									Extend Membership
								</Typography>
							</div>
							<div className={classes.groupCol}>
								<span className={clsx([classes.colTitle, membership.deactivated && 'disabled'])}>
									NUMBER OF DOGS
								</span>
								<div className={clsx([classes.mainText, membership.deactivated && 'disabled'])}>
									{type?.grandfathered_pricing ? 'Unlimited' : ''}
									{!type?.grandfathered_pricing &&
										(membership.number_of_pets > 1
											? membership.number_of_pets + ' dogs'
											: membership.number_of_pets
												? membership.number_of_pets + ' dog'
												: 'Error No Dogs')}
								</div>
								<Typography
									onClick={() => {
										if (!isManager) return
										if (!membership.deactivated && !membership.expired) {
											setMembership(membership)
											setAddDogDialogShown(true)
										}
									}}
									className={classes.editMembershipText}
									style={{
										opacity: isManager ? 1 : 0.6,
										color:
											!membership.deactivated && !membership.expired ? '#6B4AD6' : '#777679'
									}}>
									Add dogs
								</Typography>
								<Typography
									onClick={() => {
										if (!isManager) return
										if (
											!membership.deactivated &&
											!membership.expired &&
											membership.number_of_pets > 1
										) {
											setMembership(membership)
											setRemoveDogDialogShown(true)
										}
									}}
									className={classes.editMembershipText}
									style={{
										opacity: isManager ? 1 : 0.6,
										cursor: isManager ? 'pointer' : undefined,
										color:
											!membership.deactivated &&
												!membership.expired &&
												membership.number_of_pets > 1
												? '#DC2A2A'
												: '#777679'
									}}>
									Remove dogs
								</Typography>
							</div>
							{membership.type !== 'vip_trial' && (
								<div className={classes.groupCol}>
									<span
										className={clsx([
											classes.colTitle,
											membership.deactivated && 'disabled',
											membership.canceled_within_notice_period && 'disabled'
										])}>
										AUTO RENEW
									</span>
									<div
										className={clsx([
											classes.mainText,
											membership.deactivated && 'disabled',
											membership.canceled_within_notice_period && 'disabled'
										])}>
										{getRenewalStatus(membership)}
									</div>
									<Typography
										onClick={() => {
											if (!canChangeMembershipAutoRenewSetting) {
												return
											}
											if (
												!membership.deactivated &&
												!membership.expired &&
												!membership.canceled_within_notice_period
											) {
												setMembership(membership)
												setEditAutoRenewDialogShown(true)
											}
										}}
										disabled={true}
										className={classes.editMembershipText}
										style={{
											opacity: canChangeMembershipAutoRenewSetting ? 1 : 0.6,
											cursor: canChangeMembershipAutoRenewSetting ? 'pointer' : undefined,
											color:
												!membership.deactivated &&
													!membership.expired &&
													!membership.canceled_within_notice_period &&
													canChangeMembershipAutoRenewSetting
													? '#6B4AD6'
													: '#777679'
										}}>
										{membership.auto_renew ? 'Disable' : 'Enable'}
									</Typography>
									{membership.auto_renew && (
										<Typography
											onClick={() => {
												if (!isManager) return
												if (
													!membership.deactivated &&
													!membership.expired &&
													!membership.canceled_within_notice_period
												) {
													setMembership(membership)
													setSpecialRenewalDialogShown(true)
												}
											}}
											disabled={true}
											className={classes.editMembershipText}
											style={{
												opacity: isManager ? 1 : 0.6,
												cursor: isManager ? 'pointer' : undefined,
												color:
													!membership.deactivated &&
														!membership.expired &&
														!membership.canceled_within_notice_period &&
														isManager
														? '#6B4AD6'
														: '#777679',
												marginTop: '8px'
											}}>
											Special Renewal Settings
										</Typography>
									)
									}
									{!membership.deactivated &&
										!membership.auto_renew &&
										!membership.canceled_within_notice_period &&
										validMemberships.length === 1 && (
											<Box
												style={{
													display: 'flex',
													flexDirection: 'row',
													paddingTop: 20,
													alignItems: 'center'
												}}>
												<Typography
													style={{
														color: '#000000',
														fontSize: 14,
														fontWeight: '600',
														marginRight: 16
													}}>
													Mute Renewal Alert
												</Typography>
												<Switch
													checked={client && client.mute_renewal_alert ? true : false}
													onChange={() => {
														updateMute()
													}}
													name="mute_renewal_alert"
													icon={
														<div className={classes.thumb}>
															<Icon className="far fa-times" />
														</div>
													}
													checkedIcon={
														<div className={classes.thumb}>
															<Icon className="far fa-check" />
														</div>
													}
													classes={{
														root: classes.switchRoot,
														switchBase: classes.switchBase,
														thumb: classes.thumb,
														track: classes.track,
														checked: classes.checked
													}}
												/>
											</Box>
										)}
								</div>
							)}
						</div>
						{membership.promo ? (
							<div
								style={{
									width: '100%',
									height: '38px',
									backgroundColor: 'rgba(107, 74, 214, 0.1)',
									borderBottomLeftRadius: '6px',
									borderBottomRightRadius: '6px'
								}}>
								<Typography
									style={{
										fontSize: '14px',
										fontWeight: '600',
										marginLeft: '16px',
										paddingTop: '10px',
										color: '#3B0275',
										opacity: '0.75'
									}}>
									Applied Promo Code: {membership.promo.code.toUpperCase()} (+
									{membership.promo.benefit_number} free{' '}
									{membership.promo.benefit_type === 'membership_extension_months'
										? membership.promo.benefit_number === 1
											? 'month'
											: 'months'
										: ' unit of time'}
									)
								</Typography>
							</div>
						) : null}
					</div>
				)
			})
	}

	const renderToggle = () => {
		return (
			<div
				style={{
					width: '100%',
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'flex-start',
					alignItems: 'center',
					padding: 16,
					backgroundColor: '#F3F3F3',
					borderRadius: 6,
					height: 63,
					marginBottom: 16
				}}>
				<Typography
					style={{
						color: '#1a1a35',
						fontSize: 16,
						fontWeight: '600',
						marginRight: 16
					}}>
					Founding Tag
				</Typography>
				<Switch
					checked={client && client.founding_member ? true : false}
					onChange={() => {
						updateFounding()
					}}
					name="sharingKennels"
					icon={
						<div className={classes.thumb}>
							<Icon className="far fa-times" />
						</div>
					}
					checkedIcon={
						<div className={classes.thumb}>
							<Icon className="far fa-check" />
						</div>
					}
					classes={{
						root: classes.switchRoot,
						switchBase: classes.switchBase,
						thumb: classes.thumb,
						track: classes.track,
						checked: classes.checked
					}}
				/>
			</div>
		)
	}

	const renderNoActivity = () => (
		<div
			style={{
				margin: '24px',
				background: '#F3F3F3',
				borderRadius: '6px',
				height: '150px',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center'
			}}>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center'
				}}>
				<Icon className="fa fa-folder-open" style={{ fontSize: '44px', color: '#A2A1A4' }} />
				<Typography style={{ color: '#777679', fontWeight: '600', fontSize: '16px' }}>
					{' '}
					No activity has been recorded
				</Typography>
			</div>
		</div>
	)

	const upgradeableTypes = useMemo(() => {
		const typesInMarket = filterMembershipTypesForHousehold(membershipTypes, client)
			.filter((t) => t.status !== 'inactive')
			.map((m) => {
				return {
					...m,
					level: membershipUpgradeMap.find((mu) => mu.type === m.type)?.level || 0
				}
			})

		return typesInMarket
	}, [client, membershipTypes])

	const upgradeableMembershipTypes = useMemo(() => {
		if (client?.memberships && upgradeableTypes?.length) {
			const { memberships } = client
			const activeMembership = memberships.find((m) => !m.deactivated && !m.expired && !m.upcoming)

			if (!activeMembership) return false

			let currentLevel =
				membershipUpgradeMap && membershipUpgradeMap.find((m) => m.type === activeMembership.type).level

			// return upgradeableTypes.filter((t) => t.level > currentLevel)
			return upgradeableTypes
		}
	}, [client, upgradeableTypes])

	return (
		<div>
			{loading || houseLoading ? <LoadingOverlay /> : null}
			<Toolbar className={classes.toolbar}>
				<Typography variant="h6">Social Access</Typography>

				<Button
					disabled={!isManager}
					variant="outlined"
					className={clsx(classes.headerButton, classes.addButton)}
					startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
					size="large"
					onClick={() => {
						setAddMembershipDialogShown(true)
						setIsUpcoming(false)
					}}>
					Active Membership
				</Button>



				{activeMembershipsExist() &&
					upgradeableMembershipTypes &&
					upgradeableMembershipTypes.length &&
					!hasGroomingMembership() ? (
					<Button
						disabled={!isManager}
						variant="outlined"
						className={clsx(classes.headerButton, classes.addButton)}
						startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
						size="large"
						onClick={() => {
							setUpgradeMembershipDialogShown(true)
							setIsUpcoming(false)
						}}>
						Upgrade Membership
					</Button>
				) : null}
			</Toolbar>
			<Container className={classes.container} maxWidth={false}>
				{renderToggle()}
				{renderMemberships()}
			</Container>
			{activityExists() ? (
				<div>
					<Toolbar className={classes.toolbar}>
						<Typography variant="h6" style={{ fontSize: '18px' }}>
							ACTIVITY
						</Typography>
					</Toolbar>
					<CheckInsTable checkIns={client.checkIns} />
				</div>
			) : (
				renderNoActivity()
			)}
			<AddMembershipDialog
				client={client}
				isShown={isAddMembershipDialogShown}
				setIsShown={setAddMembershipDialogShown}
				addNewMembership={addNewMembership}
				upcoming={upcoming}
				currentMembership={currentMembership}
			/>
			<UpgradeMembershipDialog
				client={client}
				isShown={isUpgradeMembershipDialogShown}
				setIsShown={setUpgradeMembershipDialogShown}
				addNewMembership={addNewMembership}
				upgradeableMembershipTypes={upgradeableMembershipTypes}
				upcoming={upcoming}
				currentMembership={currentMembership}
			/>
			<EditMembershipDialog
				isShown={isEditDialogShown}
				setIsShown={setEditDialogShown}
				membership={membership}
				updateMembership={updateExistingMembership}
			/>
			<ExtendMembershipDialog
				isShown={isExtendDialogShown}
				setIsShown={setExtendDialogShown}
				membership={membership}
				updateMembership={updateExistingMembership}
			/>
			<DelayMembershipDialog
				isShown={delayDialog}
				setIsShown={setDelayDialogShown}
				membership={membership}
				updateMembership={updateExistingMembership}
			/>
			<EditAutoRenewDialog
				isShown={isEditAutoRenewDialogShown}
				setIsShown={setEditAutoRenewDialogShown}
				membership={membership}
				updateMembership={updateExistingMembership}
			/>
			<AddDogsDialog
				client={client}
				isShown={isAddDogDialogShown}
				setIsShown={setAddDogDialogShown}
				membership={membership}
				addAdditionalDogs={addAdditionalDogsToMembership}
			/>
			<RemoveDogsDialog
				isShown={isRemoveDogDialogShown}
				setIsShown={setRemoveDogDialogShown}
				membership={membership}
				removeDogs={addAdditionalDogsToMembership}
			/>
			<SpecialRenewalDialog
				isShown={isSpecialRenewalDialogShown}
				setIsShown={setSpecialRenewalDialogShown}
				membership={membership}
				updateMembership={updateExistingMembership}
			/>
		</div>
	)
}

export default Membership
